import { z } from 'zod';
import type { Cue } from '../types';
import { clampTime, formatClock } from './time';

export const cueSchema = z.object({
  id: z.string().min(1), start: z.number().nonnegative(), end: z.number().positive(),
  text: z.string(), translatedText: z.string().default(''),
}).refine((cue) => cue.end > cue.start, 'Cue end must be after start');
export const cueArraySchema = z.array(cueSchema);

export function toSrt(cues: Cue[], translated = true): string {
  return cues.map((cue, i) => `${i + 1}\n${formatClock(cue.start)} --> ${formatClock(cue.end)}\n${translated && cue.translatedText ? cue.translatedText : cue.text}\n`).join('\n');
}
export function toVtt(cues: Cue[], translated = true): string {
  return `WEBVTT\n\n${cues.map((cue) => `${formatClock(cue.start, '.')} --> ${formatClock(cue.end, '.')}\n${translated && cue.translatedText ? cue.translatedText : cue.text}\n`).join('\n')}`;
}
export function mergeSegments(chunks: Array<{ offset: number; segments: Array<{ start: number; end: number; text: string }> }>): Cue[] {
  const sorted = chunks.flatMap(({ offset, segments }) => segments.map((segment) => ({
    start: clampTime(segment.start + offset), end: clampTime(segment.end + offset), text: segment.text.trim(),
  }))).filter((segment) => segment.text && segment.end > segment.start).sort((a, b) => a.start - b.start);
  const result: Cue[] = [];
  for (const segment of sorted) {
    const previous = result.at(-1);
    const normalized = segment.text.toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
    const previousText = previous?.text.toLocaleLowerCase().replace(/[^\p{L}\p{N}]+/gu, ' ').trim();
    if (previous && previousText === normalized && segment.start <= previous.end + 1) { previous.end = Math.max(previous.end, segment.end); continue; }
    const start = previous && segment.start < previous.end ? Math.max(previous.start, previous.end - 0.02) : segment.start;
    result.push({ id: crypto.randomUUID(), start, end: Math.max(start + 0.1, segment.end), text: segment.text, translatedText: '' });
  }
  return result;
}
export function translationBatches(cues: Cue[], maxChars = 6000): Cue[][] {
  const batches: Cue[][] = []; let batch: Cue[] = []; let count = 0;
  for (const cue of cues) {
    if (batch.length && count + cue.text.length > maxChars) { batches.push(batch); batch = []; count = 0; }
    batch.push(cue); count += cue.text.length;
  }
  if (batch.length) batches.push(batch); return batches;
}
export function atempoChain(ratio: number): string {
  if (!Number.isFinite(ratio) || ratio <= 0) throw new Error('Invalid atempo ratio');
  const filters: number[] = []; let value = ratio;
  while (value > 2) { filters.push(2); value /= 2; }
  while (value < 0.5) { filters.push(0.5); value /= 0.5; }
  filters.push(value); return filters.map((part) => `atempo=${part.toFixed(5)}`).join(',');
}
